import React from 'react'
import { FloraVariants, forwardRef, styled } from '@grupoboticario/flora-react-system'
import * as TabsPrimitive from '@radix-ui/react-tabs'
import { AccessibleIcon } from '@radix-ui/react-accessible-icon'
import { HTMLFloraProps } from '@grupoboticario/flora-react-utils'

const StyledList = styled(TabsPrimitive.List, {
  flexShrink: 0,
  display: 'flex',
  overflowX: 'auto',
  color: '$nonInteractivePredominant',
  boxShadow: 'inset 0 -1px 0 0 currentColor, 0 0 0 0 currentColor',
  '&[data-orientation="vertical"]': {
    boxShadow: 'none',
    flexDirection: 'column',
    overflowY: 'auto',
  },
})

const StyledTab = styled(TabsPrimitive.Trigger, {
  display: 'flex',
  flexShrink: 0,
  alignItems: 'center',
  padding: '$3',
  color: '$nonInteractivePredominant',
  userSelect: 'none',
  fontFamily: '$body',
  fontSize: '$bodySmall',
  lineHeight: '24px',
  cursor: 'pointer',
  transition: 'box-shadow 0.3s ease-out, color 0.3s ease-out',
  backgroundColor: 'transparent',
  border: 'none',
  justifyContent: 'center',
  '&:hover:not([data-disabled])': {
    color: '$linkHover',
    boxShadow: 'inset 0 -$sizes$1 0 0 currentColor, 0 0 0 0 currentColor',
  },
  '&[data-state="active"]': {
    color: '$linkDefault', // NOSONAR: Justification for suppressing this issue
    boxShadow: 'inset 0 -$sizes$1 0 0 currentColor, 0 0 0 0 currentColor',

    '&:not(:hover)': {
      color: '$linkDefault', // NOSONAR: Justification for suppressing this issue
      fill: '$linkDefault', // NOSONAR: Justification for suppressing this issue
    },
  },

  '&[data-disabled]': {
    color: '$disabledContent',
    cursor: 'not-allowed',
  },

  '&[data-orientation="vertical"]': {
    justifyContent: 'flex-start',

    padding: '$2 $3 $2 $2',
    boxShadow: 'inset -1px 0 0 currentColor, 0 0 0 currentColor',

    '&[data-state="active"], &:hover:not([data-disabled])': {
      boxShadow: 'inset -$sizes$1 0 0 currentColor, 0 0 0 currentColor',
    },
  },
})

const StyledPanel = styled(TabsPrimitive.Content, {
  flexGrow: 1,
  padding: '$4',
  color: '$nonInteractivePredominant',
})

const StyledTabs = styled(TabsPrimitive.Root, {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',

  '&[data-orientation="vertical"]': {
    flexDirection: 'row',
  },

  variants: {
    variant: {
      alt: {
        $$color: '$colors$nonInteractiveAltPredominant',
        [`& ${StyledTab}`]: {
          color: '$$color',
          '&[data-state="active"]': {
            color: '$linkAlt',
          },
          '&[data-disabled]': {
            color: '$disabledAltContent',
          },
          '&:hover:not([data-disabled])': {
            color: '$linkAltHover',
          },
        },
        [`& ${StyledPanel}`]: {
          color: '$nonInteractiveAltPredominant',
        },
      },
    },
  },
})

export type TabProps = HTMLFloraProps<'button'> &
  FloraVariants<typeof StyledTab> &
  TabsPrimitive.TabsProps & {
    icon?: React.ReactElement
    /**
     * Must be a plain text
     */
    children: React.ReactNode
  }

/**
 * @tutorial https://flora.gb.tech/docs/components/core/tabs
 */
export const Tab = forwardRef<'button', TabProps>(
  ({ icon, children, css, ...rest }, forwardedRef) => {
    const label: string =
      (React.Children.toArray(children).find((child) => typeof child === 'string') as string) || ''

    return (
      <StyledTab
        {...rest}
        css={{
          ...(css as any),
          svg: {
            marginRight: '$2',
          },
        }}
        data-flora="tab-button"
        ref={forwardedRef}
      >
        {icon && <AccessibleIcon label={label}>{icon}</AccessibleIcon>}
        {children}
      </StyledTab>
    )
  },
)

export type TabListProps = HTMLFloraProps<'div'> & FloraVariants<typeof StyledList>

/**
 * @tutorial https://flora.gb.tech/docs/components/core/tabs
 */
export const TabList = forwardRef<'div', TabListProps>((props, forwardedRef) => {
  return <StyledList {...props} data-flora="tab-list" ref={forwardedRef} />
})

export type TabsProps = HTMLFloraProps<'div'> &
  FloraVariants<typeof StyledTabs> &
  TabsPrimitive.TabsProps

/**
 * @component
 * @name Tabs
 * @description Tabs component
 * @tutorial https://flora.gb.tech/docs/components/core/tabs
 */
export const Tabs = forwardRef<'div', TabsProps>((props, forwardedRef) => {
  return <StyledTabs {...props} data-flora="tab-container" ref={forwardedRef} />
})

export type TabPanelProps = FloraVariants<typeof StyledPanel> & TabsPrimitive.TabsContentProps

/**
 * @tutorial https://flora.gb.tech/docs/components/core/tabs
 */
export const TabPanel = forwardRef<'div', TabPanelProps>((props, forwardedRef) => {
  return <StyledPanel {...props} data-flora="tab-panel" ref={forwardedRef} />
})
